import { logActivity } from '../utils/activityLog';
import React, { useState, useEffect } from 'react';
import { fmtDate } from '../utils/format';
import { CheckCircle2, Filter, Download, Plus, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, X, XCircle, FileSpreadsheet, MoreHorizontal, Pencil, Trash2 } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { transactionsService } from '../services/transactions';
import { exportService } from '../services/export';
import { toast } from 'sonner';
import { getCurrentUser, hasPermission } from '../utils/roles';
import { Segmented, useMoneyPendingCounts } from '../components/transactions/FinancePanels';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../components/ui/dropdown-menu';

const Transactions = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [transactions, setTransactions] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingTxn, setEditingTxn] = useState(null);
  const [filters, setFilters] = useState({ type: '', status: '', department: '', dateFrom: '', dateTo: '' });
  const [amountDrafts, setAmountDrafts] = useState({});
  const [savingStatusId, setSavingStatusId] = useState(null);
  const [savingAmountId, setSavingAmountId] = useState(null);
  const [formData, setFormData] = useState({ clientId: '', type: 'deposit', amount: 0, currency: 'USD', status: 'pending', description: '' });
  const { t } = useLanguage();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const currentUser = getCurrentUser();
  const canCreateTransaction = hasPermission(currentUser, 'Create Transaction');
  const canEditTransaction = hasPermission(currentUser, 'Edit Transaction');
  const canDeleteTransaction = hasPermission(currentUser, 'Delete Transaction');
  const canExportTransactions = hasPermission(currentUser, 'Export Transactions');
  const canAccessMoney = hasPermission(currentUser, 'View Finance');
  const { pendingCounts, loadPendingCounts } = useMoneyPendingCounts();

  const typeLabel = (type) => t(`transactions.types.${type}`) || type;
  const statusLabel = (status) => t(`transactions.statuses.${status}`) || status;
  const statusClass = (status) => {
    if (status === 'completed') return 'bg-blue-500/20 text-blue-400';
    if (status === 'rejected') return 'bg-red-500/20 text-red-400';
    return 'bg-yellow-500/20 text-yellow-400';
  };
  const amountClass = (type) => {
    if (type === 'withdrawal') return isDark ? 'text-red-400' : 'text-red-600';
    return isDark ? 'text-green-400' : 'text-green-600';
  };
  // Format a stored (UTC) timestamp for a <input type="datetime-local"> value.
  const toLocalInput = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  useEffect(() => { logActivity('view', 'transactions', null, 'Viewed Transactions list'); }, []);

  useEffect(() => {
    loadTransactions();
  }, [currentPage, itemsPerPage, filters]);

  const loadTransactions = async () => {
    try {
      setLoading(true);
      const data = await transactionsService.getTransactions(currentPage, itemsPerPage, filters);
      const nextTransactions = data.transactions || [];
      setTransactions(nextTransactions);
      setAmountDrafts(nextTransactions.reduce((acc, txn) => ({ ...acc, [txn.id]: String(txn.amount ?? 0) }), {}));
      setTotal(data.total);
      setTotalPages(data.totalPages);
    } catch (error) {
      toast.error(t('transactions.failedLoad'));
    } finally {
      setLoading(false);
    }
  };

  const updateFilter = (patch) => {
    setCurrentPage(1);
    setFilters(prev => ({ ...prev, ...patch }));
  };

  const handleExport = async (format) => {
    if (!canExportTransactions) return;
    try {
      const blob = await transactionsService.exportTransactions(format, filters);
      exportService.downloadBlob(blob, `transactions.${format}`);
      toast.success(t('transactions.exported').replace('{format}', format.toUpperCase()));
    } catch (error) {
      toast.error(t('transactions.exportFailed'));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (editingTxn && !canEditTransaction) return;
    if (!editingTxn && !canCreateTransaction) return;
    try {
      if (editingTxn) {
        await transactionsService.updateTransaction(editingTxn.id, formData);
        // Date lives on a dedicated, audited (manager-only) endpoint — only call it when changed.
        if (formData.date && new Date(formData.date).getTime() !== new Date(editingTxn.createdAt).getTime()) {
          await transactionsService.updateTransactionDate(editingTxn.id, new Date(formData.date).toISOString());
        }
        toast.success(t('transactions.transactionUpdated'));
      } else {
        await transactionsService.createTransaction(formData);
        toast.success(t('transactions.transactionCreated'));
      }
      setShowModal(false);
      setEditingTxn(null);
      loadTransactions();
      loadPendingCounts();
    } catch (error) {
      toast.error(t('transactions.operationFailed'));
    }
  };

  const handleDelete = async (id) => {
    if (!canDeleteTransaction) return;
    if (window.confirm(t('transactions.confirmDelete'))) {
      try {
        await transactionsService.deleteTransaction(id);
        toast.success(t('transactions.transactionDeleted'));
        loadTransactions();
        loadPendingCounts();
      } catch (error) {
        toast.error(t('transactions.deleteFailed'));
      }
    }
  };

  const openEdit = (txn) => {
    if (!canEditTransaction) return;
    setEditingTxn(txn);
    setFormData({ clientId: txn.clientId, type: txn.type, amount: txn.amount, currency: txn.currency, status: txn.status, description: txn.description || '', date: toLocalInput(txn.createdAt) });
    setShowModal(true);
  };

  const saveAmount = async (txn, { silent = false } = {}) => {
    const draft = amountDrafts[txn.id] ?? txn.amount;
    const nextAmount = Number(draft);
    if (!Number.isFinite(nextAmount) || nextAmount <= 0) {
      toast.error('Amount must be greater than 0');
      throw new Error('Invalid amount');
    }
    if (Number(txn.amount) === nextAmount) return txn;

    setSavingAmountId(txn.id);
    try {
      const updated = await transactionsService.updateTransactionAmount(txn.id, nextAmount);
      setTransactions(prev => prev.map(row => row.id === txn.id ? updated : row));
      setAmountDrafts(prev => ({ ...prev, [txn.id]: String(updated.amount ?? 0) }));
      if (!silent) toast.success('Amount saved');
      return updated;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save amount');
      throw e;
    } finally {
      setSavingAmountId(null);
    }
  };

  const changeStatus = async (txn, nextStatus) => {
    setSavingStatusId(txn.id);
    try {
      const txnToUpdate = nextStatus === 'completed' && txn.status === 'pending'
        ? await saveAmount(txn, { silent: true })
        : txn;
      const updated = await transactionsService.updateTransactionStatus(txnToUpdate.id, nextStatus);
      setTransactions(prev => (
        !filters.status || updated.status === filters.status
          ? prev.map(row => row.id === txn.id ? updated : row)
          : prev.filter(row => row.id !== txn.id)
      ));
      setAmountDrafts(prev => ({ ...prev, [updated.id]: String(updated.amount ?? 0) }));
      loadPendingCounts();
      toast.success(`Status changed to ${nextStatus}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to change status');
    } finally {
      setSavingStatusId(null);
    }
  };

  const statusMenuItems = (txn) => {
    if (txn.status === 'completed') return [{ status: 'rejected', label: 'Reject', icon: XCircle, className: 'text-red-400 focus:text-red-400' }];
    if (txn.status === 'rejected') return [{ status: 'completed', label: 'Complete', icon: CheckCircle2, className: 'text-emerald-400 focus:text-emerald-400' }];
    return [
      { status: 'completed', label: 'Complete', icon: CheckCircle2, className: 'text-emerald-400 focus:text-emerald-400' },
      { status: 'rejected', label: 'Reject', icon: XCircle, className: 'text-red-400 focus:text-red-400' },
    ];
  };

  const btnClass = `flex items-center gap-2 px-4 py-2 border rounded-md transition-colors ${
    isDark ? 'bg-[#0a0e1a] border-gray-700 text-gray-300 hover:bg-[#1a1f3a]' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
  }`;

  const inputClass = `w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${
    isDark ? 'bg-[#1a1f3a] border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'
  }`;

  const moneyAmountInputClass = `h-8 w-28 rounded-md border px-2 text-sm font-semibold tabular-nums focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-60 ${isDark ? 'border-gray-700 bg-[#0a0e1a]' : 'border-gray-300 bg-white'}`;

  if (loading && transactions.length === 0) {
    return <div className={`flex items-center justify-center h-screen ${isDark ? 'text-white' : 'text-gray-900'}`}>{t('transactions.loading')}</div>;
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div className="flex flex-wrap items-center gap-3">
          <button onClick={() => setShowFilters(!showFilters)} className={btnClass} data-testid="filter-btn">
            <Filter size={18} />
            <span>{t('transactions.filters')}</span>
          </button>
          {canAccessMoney && (
            <>
              <Segmented
                value={filters.department || 'all'}
                onChange={(value) => updateFilter({ department: value === 'all' ? '' : value })}
                isDark={isDark}
                items={[
                  { key: 'all', label: 'All' },
                  { key: 'sales', label: 'Sales', badge: pendingCounts.sales },
                  { key: 'retention', label: 'Retention', badge: pendingCounts.retention },
                ]}
              />
              <Segmented
                value={filters.status || 'all'}
                onChange={(value) => updateFilter({ status: value === 'all' ? '' : value })}
                isDark={isDark}
                items={[
                  { key: 'pending', label: 'Pending', badge: pendingCounts.total },
                  { key: 'completed', label: 'Completed' },
                  { key: 'rejected', label: 'Rejected' },
                  { key: 'all', label: 'All' },
                ]}
              />
            </>
          )}
        </div>
        {(canExportTransactions || canCreateTransaction) && (
          <div className="flex items-center gap-3">
            {canExportTransactions && (
              <>
                <button onClick={() => handleExport('csv')} className={btnClass} data-testid="export-csv-btn">
                  <Download size={18} />
                  <span>CSV</span>
                </button>
                <button onClick={() => handleExport('xlsx')} className={btnClass} data-testid="export-xlsx-btn">
                  <FileSpreadsheet size={18} />
                  <span>Excel</span>
                </button>
              </>
            )}
            {canCreateTransaction && (
              <button onClick={() => { setEditingTxn(null); setFormData({ clientId: '', type: 'deposit', amount: 0, currency: 'USD', status: 'pending', description: '' }); setShowModal(true); }}
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700" data-testid="new-txn-btn">
                <Plus size={18} />
                <span>{t('transactions.new')}</span>
              </button>
            )}
          </div>
        )}
      </div>

      {showFilters && (
        <div className={`mb-6 p-4 border rounded-lg ${isDark ? 'bg-[#0a0e1a] border-gray-800' : 'bg-white border-gray-200'}`}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className={`block text-sm mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{t('transactions.type')}</label>
              <select value={filters.type} onChange={(e) => updateFilter({ type: e.target.value })} className={inputClass} data-testid="filter-type">
                <option value="">{t('transactions.all')}</option>
                <option value="deposit">{typeLabel('deposit')}</option>
                <option value="withdrawal">{typeLabel('withdrawal')}</option>
              </select>
            </div>
            <div>
              <label className={`block text-sm mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{t('transactions.dateFrom')}</label>
              <input type="date" value={filters.dateFrom?.split('T')[0] || ''} onChange={(e) => updateFilter({ dateFrom: e.target.value ? new Date(e.target.value).toISOString() : '' })} className={inputClass} data-testid="filter-date-from" />
            </div>
            <div>
              <label className={`block text-sm mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{t('transactions.dateTo')}</label>
              <input type="date" value={filters.dateTo?.split('T')[0] || ''} onChange={(e) => updateFilter({ dateTo: e.target.value ? new Date(e.target.value + 'T23:59:59').toISOString() : '' })} className={inputClass} data-testid="filter-date-to" />
            </div>
          </div>
          <button onClick={() => updateFilter({ type: '', status: '', department: '', dateFrom: '', dateTo: '' })} className="mt-3 text-sm text-purple-500 hover:text-purple-400">{t('transactions.clearFilters')}</button>
        </div>
      )}

      <div className={`border rounded-lg overflow-hidden ${isDark ? 'bg-[#0a0e1a] border-gray-800' : 'bg-white border-gray-200'}`}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="transactions-table">
            <thead>
              <tr className={`border-b ${isDark ? 'bg-[#1a1f3a] border-gray-800' : 'bg-gray-50 border-gray-200'}`}>
                <th className={`text-left px-4 py-3 font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>ID</th>
                <th className={`text-left px-4 py-3 font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{t('transactions.clientId')}</th>
                <th className={`text-left px-4 py-3 font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{t('transactions.type')}</th>
                <th className={`text-left px-4 py-3 font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{t('transactions.amount')}</th>
                <th className={`text-left px-4 py-3 font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{t('transactions.currency')}</th>
                <th className={`text-left px-4 py-3 font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{t('transactions.status')}</th>
                <th className={`text-left px-4 py-3 font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{t('transactions.description')}</th>
                <th className={`text-left px-4 py-3 font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{t('transactions.date')}</th>
                {(canEditTransaction || canDeleteTransaction || canAccessMoney) && (
                  <th className={`text-left px-4 py-3 font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{t('transactions.actions')}</th>
                )}
              </tr>
            </thead>
            <tbody>
              {transactions.map((txn) => (
                <tr key={txn.id} className={`border-b transition-colors ${isDark ? 'border-gray-800/50 hover:bg-[#1a1f3a]/30' : 'border-gray-100 hover:bg-gray-50'}`} data-testid={`txn-row-${txn.id}`}>
                  <td className={`px-4 py-3 font-mono text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{txn.id.slice(0, 8)}...</td>
                  <td className={`px-4 py-3 ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>{txn.clientId}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${txn.type === 'deposit' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                      {typeLabel(txn.type)}
                    </span>
                  </td>
                  <td className={`px-4 py-3 font-medium ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>
                    {canAccessMoney && txn.status === 'pending' ? (
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={amountDrafts[txn.id] ?? txn.amount}
                        disabled={savingStatusId === txn.id || savingAmountId === txn.id}
                        onChange={e => setAmountDrafts(prev => ({ ...prev, [txn.id]: e.target.value }))}
                        onBlur={() => saveAmount(txn).catch(() => {})}
                        onKeyDown={e => {
                          if (e.key === 'Enter') e.currentTarget.blur();
                          if (e.key === 'Escape') setAmountDrafts(prev => ({ ...prev, [txn.id]: String(txn.amount ?? 0) }));
                        }}
                        className={`${moneyAmountInputClass} ${amountClass(txn.type)}`}
                      />
                    ) : (
                      <span className={`font-semibold tabular-nums ${amountClass(txn.type)}`}>{txn.amount.toLocaleString()}</span>
                    )}
                  </td>
                  <td className={`px-4 py-3 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{txn.currency}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${statusClass(txn.status)}`}>
                      {statusLabel(txn.status)}
                    </span>
                  </td>
                  <td className={`px-4 py-3 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{txn.description || '-'}</td>
                  <td className={`px-4 py-3 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{fmtDate(txn.createdAt)}</td>
                  {(canEditTransaction || canDeleteTransaction || canAccessMoney) && (
                    <td className="px-4 py-3">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            type="button"
                            title="Transaction actions"
                            aria-label="Transaction actions"
                            className={`inline-flex h-8 w-8 items-center justify-center rounded-md border transition-colors ${isDark ? 'border-gray-700 text-gray-300 hover:bg-[#1a1f3a]' : 'border-gray-200 text-gray-700 hover:bg-gray-50'}`}
                            data-testid={`txn-actions-${txn.id}`}
                          >
                            <MoreHorizontal size={16} />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className={`${isDark ? 'border-gray-800 bg-[#0a0e1a] text-gray-200' : 'border-gray-200 bg-white text-gray-900'}`}>
                          {canAccessMoney && statusMenuItems(txn).map(item => {
                            const Icon = item.icon;
                            return (
                              <DropdownMenuItem
                                key={item.status}
                                disabled={savingStatusId === txn.id || savingAmountId === txn.id}
                                onSelect={() => changeStatus(txn, item.status)}
                                className={item.className}
                              >
                                <Icon size={15} />
                                <span>{item.label}</span>
                              </DropdownMenuItem>
                            );
                          })}
                          {canAccessMoney && (canEditTransaction || canDeleteTransaction) && <DropdownMenuSeparator className={isDark ? 'bg-gray-800' : 'bg-gray-200'} />}
                          {(canEditTransaction || canDeleteTransaction) && (
                            <>
                              {canEditTransaction && (
                                <DropdownMenuItem onSelect={() => openEdit(txn)} className="text-purple-400 focus:text-purple-400" data-testid={`edit-txn-${txn.id}`}>
                                  <Pencil size={15} />
                                  <span>{t('transactions.edit')}</span>
                                </DropdownMenuItem>
                              )}
                              {canDeleteTransaction && (
                                <DropdownMenuItem onSelect={() => handleDelete(txn.id)} className="text-red-400 focus:text-red-400" data-testid={`delete-txn-${txn.id}`}>
                                  <Trash2 size={15} />
                                  <span>{t('transactions.delete')}</span>
                                </DropdownMenuItem>
                              )}
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className={`flex items-center justify-between px-6 py-4 border-t ${isDark ? 'bg-[#0a0e1a] border-gray-800' : 'bg-white border-gray-200'}`}>
          <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            {t('transactions.showing')} {((currentPage - 1) * itemsPerPage) + 1} {t('transactions.to')} {Math.min(currentPage * itemsPerPage, total)} {t('transactions.of')} {total}
          </span>
          <div className="flex items-center gap-2">
            <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1} className={`p-2 border rounded disabled:opacity-50 ${isDark ? 'bg-[#1a1f3a] border-gray-700 text-gray-300' : 'bg-white border-gray-300'}`}>
              <ChevronsLeft size={16} />
            </button>
            <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className={`p-2 border rounded disabled:opacity-50 ${isDark ? 'bg-[#1a1f3a] border-gray-700 text-gray-300' : 'bg-white border-gray-300'}`}>
              <ChevronLeft size={16} />
            </button>
            <span className="px-4 py-2 bg-purple-600 text-white rounded">{currentPage}</span>
            <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className={`p-2 border rounded disabled:opacity-50 ${isDark ? 'bg-[#1a1f3a] border-gray-700 text-gray-300' : 'bg-white border-gray-300'}`}>
              <ChevronRight size={16} />
            </button>
            <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} className={`p-2 border rounded disabled:opacity-50 ${isDark ? 'bg-[#1a1f3a] border-gray-700 text-gray-300' : 'bg-white border-gray-300'}`}>
              <ChevronsRight size={16} />
            </button>
          </div>
        </div>
      </div>

      {showModal && ((editingTxn && canEditTransaction) || (!editingTxn && canCreateTransaction)) && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="txn-modal">
          <div className={`w-full max-w-md p-6 rounded-lg ${isDark ? 'bg-[#1a1f3a]' : 'bg-white'}`}>
            <div className="flex items-center justify-between mb-4">
              <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{editingTxn ? t('transactions.editTransaction') : t('transactions.newTransaction')}</h2>
              <button onClick={() => setShowModal(false)} className={isDark ? 'text-gray-400' : 'text-gray-600'}><X size={20} /></button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className={`block text-sm mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{t('transactions.clientId')}</label>
                <input type="text" value={formData.clientId} onChange={(e) => setFormData({...formData, clientId: e.target.value})} className={inputClass} required data-testid="txn-client-id" />
              </div>
              <div>
                <label className={`block text-sm mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{t('transactions.type')}</label>
                <select value={formData.type} onChange={(e) => setFormData({...formData, type: e.target.value})} className={inputClass} data-testid="txn-type">
                  <option value="deposit">{typeLabel('deposit')}</option>
                  <option value="withdrawal">{typeLabel('withdrawal')}</option>
                </select>
              </div>
              <div>
                <label className={`block text-sm mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{t('transactions.amount')}</label>
                <input type="number" value={formData.amount} onChange={(e) => setFormData({...formData, amount: parseFloat(e.target.value)})} className={inputClass} required data-testid="txn-amount" />
              </div>
              <div>
                <label className={`block text-sm mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{t('transactions.status')}</label>
                <select value={formData.status} onChange={(e) => setFormData({...formData, status: e.target.value})} className={inputClass} data-testid="txn-status">
                  <option value="pending">{statusLabel('pending')}</option>
                  <option value="completed">{statusLabel('completed')}</option>
                  <option value="rejected">{statusLabel('rejected')}</option>
                </select>
              </div>
              {editingTxn && (
                <div>
                  <label className={`block text-sm mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{t('transactions.date')}</label>
                  <input type="datetime-local" value={formData.date || ''} onChange={(e) => setFormData({...formData, date: e.target.value})} className={inputClass} data-testid="txn-date" />
                </div>
              )}
              <div>
                <label className={`block text-sm mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{t('transactions.description')}</label>
                <input type="text" value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} className={inputClass} placeholder={t('transactions.descriptionPlaceholder')} data-testid="txn-description" />
              </div>
              <button type="submit" className="w-full py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700" data-testid="txn-submit">{editingTxn ? t('transactions.update') : t('transactions.create')}</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Transactions;
